/**
 * 精简版 UA 解析器，仅保留设备识别能力。
 * 参考 `src/main/ua-parser.js` 的 getDevice 相关实现。
 */
const UA_MAX_LENGTH = 500;
const EMPTY = '';

const TYPE = 'type';
const MODEL = 'model';
const VENDOR = 'vendor';
const APP_NAME = 'appName';
const APP_VERSION = 'appVersion';
const BUILD_NUMBER = 'buildNumber';

export type AppType = 'h5' | 'android' | 'ios';
export type DeviceType = "desktop" | "ios" | "android" | "h5";

type DeviceTypeValue =
    | 'console'
    | 'desktop'
    | 'embedded'
    | 'mobile'
    | 'smarttv'
    | 'tablet'
    | 'wearable'
    | 'xr';

const MOBILE: DeviceTypeValue = 'mobile';
const TABLET: DeviceTypeValue = 'tablet';
const SMARTTV: DeviceTypeValue = 'smarttv';
const CONSOLE: DeviceTypeValue = 'console';
const DESKTOP: DeviceTypeValue = 'desktop';

const SAMSUNG = 'Samsung';
const APPLE = 'Apple';
const GOOGLE = 'Google';
const AMAZON = 'Amazon';
const MICROSOFT = 'Microsoft';

const ANDROID = 'android';
const IOS = 'ios';

const isWindow = typeof window === 'object';
const NAVIGATOR = isWindow && window.navigator ? window.navigator : undefined;
const DEFAULT_UA = NAVIGATOR && NAVIGATOR.userAgent ? NAVIGATOR.userAgent : '';

export interface DeviceInfo {
    type?: DeviceTypeValue;
    model?: string;
    vendor?: string;
    appName?: string;
    appVersion?: string;
    buildNumber?: string;
    os?: 'android' | 'ios';
}

type DeviceRegexMap = any[];

function isString(val: unknown): val is string {
    return typeof val === 'string';
}

function strip(pattern: RegExp, str: string): string {
    return str.replace(pattern, EMPTY);
}

function trim(str: unknown, len?: number): string {
    const value = strip(/^\s\s*/, String(str));
    return typeof len === 'number' ? value.substring(0, len) : value;
}

function resolveUA(ua?: string): string {
    const target = isString(ua) ? ua : DEFAULT_UA;
    return trim(target, UA_MAX_LENGTH);
}

function rgxMapper(ua: string, arrays: DeviceRegexMap, data: Record<string, any>): void {
    if (!ua || !arrays) {return;}

    for (let i = 0; i < arrays.length; i += 2) {
        const regexes = arrays[i] as RegExp[];
        const props = arrays[i + 1] as any[];

        let matches: RegExpExecArray | null = null;

        for (let j = 0; j < regexes.length && !matches; j += 1) {
            const pattern = regexes[j];
            if (!pattern) {continue;}
            matches = pattern.exec(ua);
            if (!matches) {continue;}

            let groupIndex = 1;
            for (let p = 0; p < props.length; p += 1) {
                const prop = props[p];
                const matchValue = matches[groupIndex] ? matches[groupIndex] : undefined;

                if (Array.isArray(prop)) {
                    const key = prop[0];
                    const value = prop[1];

                    if (typeof value === 'function') {
                        data[key] = value(matchValue);
                    } else if (value === undefined) {
                        data[key] = matchValue;
                    } else {
                        data[key] = value;
                    }
                } else {
                    data[prop] = matchValue;
                }

                groupIndex += 1;
            }
        }

        if (matches) {
            break;
        }
    }
}

/**
 * 检测操作系统类别（Android 或 iOS）
 * @param ua User-Agent 字符串
 * @returns 'android' | 'ios' | undefined
 */
function detectOS(ua: string): 'android' | 'ios' | undefined {
    if (!ua) {
        return undefined;
    }

    // 检测 iOS 系统
    // 匹配 iPhone、iPad、iPod 以及 iOS 版本号
    if (/iPhone|iPad|iPod/i.test(ua) || /iOS|iPhone OS|iPadOS/i.test(ua)) {
        return IOS;
    }

    // 检测 Android 系统
    // 匹配 Android 关键字
    if (/Android/i.test(ua)) {
        return ANDROID;
    }

    return undefined;
}

const DEFAULT_DEVICE_REGEXES: DeviceRegexMap = [
    [
        /\b(sm-t\d{3}[a-z0-9]*)\b/i,
    ],
    [MODEL, [VENDOR, SAMSUNG], [TYPE, TABLET]],

    [
        /\b(sm-[a-z0-9]{3,})\b/i,
        /samsung[- ]([-\w]+)/i,
    ],
    [MODEL, [VENDOR, SAMSUNG], [TYPE, MOBILE]],

    [
        /(?:\/|\()(ip(?:hone|od)[\w, ]*)[\/\);]/i,
    ],
    [MODEL, [VENDOR, APPLE], [TYPE, MOBILE]],

    [
        /\b(?:ios|apple\w+)\/.+[\(\/](ipad)/i,
        /\b(ipad)[\d,]*[;\] ].+(mac |i(pad)?)os/i,
    ],
    [MODEL, [VENDOR, APPLE], [TYPE, TABLET]],

    [
        /droid.+; ?(pixel [^;\)]+)(?: build|\))/i,
    ],
    [MODEL, [VENDOR, GOOGLE], [TYPE, MOBILE]],

    [
        /(kindle|kf[a-z0-9]+)/i,
    ],
    [MODEL, [VENDOR, AMAZON], [TYPE, TABLET]],

    [
        /\b(playstation \w+)\b/i,
    ],
    [MODEL, [VENDOR, 'Sony'], [TYPE, CONSOLE]],

    [
        /\b(xbox(?: one)?(?!; xbox))[\); ]/i,
    ],
    [MODEL, [VENDOR, MICROSOFT], [TYPE, CONSOLE]],

    [
        /(smart[- ]?tv|hbbtv|netcast|viera)/i,
    ],
    [[TYPE, SMARTTV]],

    [
        /droid.+; ([^;]+?)(?: build|\))/i,
    ],
    [MODEL, [VENDOR, 'Generic'], [TYPE, MOBILE]],

    [
        /\b(iphone|ipad|ipod)\b/i,
    ],
    [[MODEL, (match: string | undefined) => (match ? match.toLowerCase() : match)], [VENDOR, APPLE]],

    // 桌面浏览器识别（放在最后，作为兜底规则）
    // Mac 桌面
    [
        /macintosh/i,
    ],
    [[TYPE, DESKTOP], [VENDOR, APPLE]],
    // Windows 桌面
    [
        /windows nt/i,
    ],
    [[TYPE, DESKTOP], [VENDOR, MICROSOFT]],
    // Linux/Unix 等其他桌面系统
    [
        /linux|unix|bsd/i,
    ],
    [[TYPE, DESKTOP]],
];

class UADeviceParser {
    private ua: string;
    private readonly regexes: DeviceRegexMap = DEFAULT_DEVICE_REGEXES;

    constructor(ua?: string) {
        this.ua = resolveUA(ua);
    }

    public setUA(ua: string): this {
        this.ua = resolveUA(ua);
        return this;
    }

    public getUA(): string {
        return this.ua;
    }

    public getDevice(): DeviceInfo {
        const data: Record<string, any> = {};
        
        // 先提取设备信息
        rgxMapper(this.ua, this.regexes, data);

        // 单独提取 EdgenApp 应用信息（不影响设备识别）
        // 格式: EdgenApp/appVersion/buildNumber
        const edgenAppMatch = this.ua.match(/EdgenApp\/([^\/]+)\/([^\s]+)/i);
        if (edgenAppMatch) {
            data[APP_NAME] = 'EdgenApp';
            data[APP_VERSION] = edgenAppMatch[1]; // appVersion
            data[BUILD_NUMBER] = edgenAppMatch[2]; // buildNumber
        }

        // 检测操作系统类别（Android 或 iOS）
        const os = detectOS(this.ua);

        const modelValue = data[MODEL];
        const model = modelValue !== undefined ? String(modelValue).trim() : undefined;
        const vendor = data[VENDOR];
        const type = data[TYPE] || (vendor || model ? MOBILE : undefined);
        
        // 注意：现代 iPad（iPadOS 13+）的 UA 伪装成 Mac，无法通过 UA 准确识别
        // 实际判断应该依赖 cookie（x-is-native-app）或 URL 参数（is_native_app）
        // 服务端检测逻辑已在 ProxyHandler.ts 中添加降级方案
        const appName = data[APP_NAME];
        const appVersion = data[APP_VERSION];
        const buildNumber = data[BUILD_NUMBER];

        return {
            type,
            model,
            vendor,
            appName,
            appVersion,
            buildNumber,
            os,
        };
    }
}

export function getDevice(ua?: string): DeviceInfo {
    return new UADeviceParser(ua).getDevice();
}

